// progress.service.ts
import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

// Define an enum for the plan steps
export enum PlanStep {
  WorkPackages = 'plan_work_packages',
  WorkPackagesCostEstimates = 'plan_work_packages_cost_estimates',
  WorkPackagesRaci = 'plan_work_packages_raci',
  RiskAssessment = 'plan_risk_assessment',
}

@Injectable({
  providedIn: 'root',
})
export class ProgressService {
  clearForms() {
    this.projectBudgetTimelineForm.reset();
    this.projectOverviewForm.reset();
    this.projectLocationSiteConditionsForm.reset();
    this.progressSource.next(0);
    this.progressSource2.next(0);
  }
  
  private progressSource = new BehaviorSubject<number>(0);
  progress$ = this.progressSource.asObservable();
  projectOverviewForm: FormGroup;
  projectLocationSiteConditionsForm: FormGroup;
  projectBudgetTimelineForm: FormGroup;
  private progressMap = new Map<string, any>();

  constructor(private fb: FormBuilder) {
    this.createProjectOverviewForm();
    this.createProjectLocationSiteConditionsForm();
    this.createBudgetTimelineForm();

    this.progressMap.set('projectTypeParantId', null);
    this.progressMap.set('countryId', null);
    this.progressMap.set('uploadedFiles', null);
    this.progressMap.set('uploadedLocationFiles', null);
    this.progressMap.set('grade', null);
    this.progressMap.set('selectedStakeholders', null);
    this.progressMap.set('keyDeliverables', null);

    // this.progressMap.delete('currentKeyDeliverable');

    this.calculateCompletionPercentage();

    this.initializePlanProgress();
  }

  updateProgress1(value: number) {
    this.progressSource.next(value);
  }
  getProgressValue(): number {
    return this.progressSource.getValue();
  }
  private progressSource2 = new BehaviorSubject<number>(0);
  progress2$ = this.progressSource2.asObservable();

  updateProgress2(value: number) {
    this.progressSource2.next(value);
  }

  private progressSource3 = new BehaviorSubject<number>(0);
  progress3$ = this.progressSource3.asObservable();

  updateProgress3(value: number) {
    this.progressSource3.next(value);
  }

  
  updateProgressMetrics(field: string, value: any) {
    //throw new Error('Method not implemented.');
    // console.log('field:value', field, value);
    // Check if the map already has the key
    
    // If not, add the key-value pair
    this.progressMap.set(field, value);
    const progress = this.calculateCompletionPercentage();
    this.progressSource.next(progress);
  }

  // Helper method for nested FormGroup inside FormArray or nested FormGroup
  // Helper method for nested FormGroup inside FormArray or nested FormGroup
  private attachValueChangeListenersForFormGroup(group: FormGroup, parentPath: string): void {
    Object.keys(group.controls).forEach((nestedField) => {
      const control = group.get(nestedField);
      if (control instanceof FormControl) {
        const initialValue = control.value;
        const fieldName = `${parentPath}.${nestedField}`;

        // Initialize progressMap with initial values
        this.updateProgressMetrics(fieldName, initialValue);

        // Attach listener for value changes
        control.valueChanges.subscribe((value) => {
          this.updateProgressMetrics(fieldName, value);
        });
      } else if (control instanceof FormArray) {
        // If the nested control is a FormArray, process it similarly
        control.controls.forEach((nestedGroup, index) => {
          if (nestedGroup instanceof FormGroup) {
            this.attachValueChangeListenersForFormGroup(nestedGroup, `${parentPath}.${nestedField}[${index}]`);
          }
        });
      }
    });
  }

  // Method to attach value change listeners for all fields in a FormGroup
  attachValueChangeListeners(form: FormGroup): void {
    Object.keys(form.controls).forEach((field) => {
      const control = form.get(field);

      if (control instanceof FormControl) {
        const initialValue = control.value;
        
        // Initialize progressMap with initial values
        this.updateProgressMetrics(field, initialValue);

        // Attach listener for value changes
        control.valueChanges.subscribe((value) => {
          this.updateProgressMetrics(field, value);
        });
      } else if (control instanceof FormArray) {
        // Attach listener for FormArray controls
        control.controls.forEach((group, index) => {
          if (group instanceof FormGroup) {
            this.attachValueChangeListenersForFormGroup(group, `${field}[${index}]`);
          }
        });
      }
    });
  }

  public getProjectOverviewForm(): FormGroup {
    if(!this.projectOverviewForm){
      this.createProjectOverviewForm();
    }
    return this.projectOverviewForm;

  }

  private createProjectOverviewForm() {
    const form = new FormGroup({
      name: new FormControl('', Validators.required),
      outline: new FormControl(''),
      currentKeyDeliverable: new FormControl(''),
    });

    this.attachValueChangeListeners(form);
    this.projectOverviewForm = form;
  }

  public getProjectLocationSiteConditionsForm(): FormGroup {
    if(!this.projectLocationSiteConditionsForm){
      this.createProjectLocationSiteConditionsForm();
    }
    return this.projectLocationSiteConditionsForm;

  }

  private createProjectLocationSiteConditionsForm() {
    const form = new FormGroup({
      city: new FormControl(''),
      street: new FormControl(''),
      zipCode: new FormControl(''),
      latitude: new FormControl(''),
      longitude: new FormControl(''),
      siteconditions: new FormControl(''),
    });
    this.attachValueChangeListeners(form);
    this.projectLocationSiteConditionsForm = form;
  }

  public getProjectBudgetTimelineForm(): FormGroup {
    if(!this.projectBudgetTimelineForm) {
      this.createBudgetTimelineForm();
    }
    return this.projectBudgetTimelineForm;
  }

  private createBudgetTimelineForm() {
    const form = new FormGroup({
      mileStonesData: this.fb.array([
        this.fb.group({
          name: [''],
          start_date: [''],
          end_date: [''],
        }),
      ]),
      name: new FormControl(''),
      projectStartDate: new FormControl(''),
      projectEndDate: new FormControl(''),
      allocated: new FormControl(null),
      showGMPFields: new FormControl(false),
      capbudget: new FormControl(null),
      rcbudget: new FormControl(null),
      mileStonesCheck: new FormControl(false),
    });
    this.attachValueChangeListeners(form);
    this.projectBudgetTimelineForm = form;
  }

  private shouldCountField(field: string, value: any): boolean {
    // const tagRegex = /<[^>]*>/g;
    // const cleanValue = (value: string): string => value?.replace(tagRegex, '') || '';
  
    // if (field === 'outline' || field === 'siteconditions') {
    //   return !!cleanValue(value);
    // }
    return !!value;
  }
  
  calculateCompletionPercentage(): number {
    let totalFields = 0;
    let completedFields = 0;
  
    const filteredKeys = ['currentKeyDeliverable', 'rcbudget', 'showGMPFields', 'capbudget', 'mileStonesCheck'];
    const filteredKeysWithPrefixes = ['mileStonesData'];
    const mapUsedToCalculate = new Map<string, any>();
  
    // Check if milestone validation is required
    // ({ totalFields, completedFields } = this.processMileStonesData(totalFields, completedFields));
  
    // Iterate over the progressMap to check each field
    this.progressMap.forEach((value, field) => {
      // Check if the field is in filteredKeys or starts with any prefix in filteredKeysWithPrefixes
      const isFiltered =
        filteredKeys.includes(field) ||
        filteredKeysWithPrefixes.some(prefix => field.startsWith(prefix));
  
      if (!isFiltered) {
        mapUsedToCalculate.set(field, value);
        if (this.shouldCountField(field, value)) {
          completedFields++;
        }
        totalFields++;
      }
    });
  
    // Calculate progress as a percentage
    const progress = totalFields > 0 ? (completedFields / totalFields) * 100 : 0;
    return progress;
  }

  private processMileStonesData(totalFields: number, completedFields: number) {
    const mileStonesFormArray = this.projectBudgetTimelineForm.get('mileStonesData') as FormArray;
    const mileStonesCheck = this.progressMap.get('mileStonesCheck');
    let milestoneValid = true;

    if (mileStonesCheck && mileStonesFormArray && mileStonesFormArray.length > 0) {
      // Validate the first milestone in the FormArray
      const firstMilestone = mileStonesFormArray.at(0).value; // Get the object at index 0
      const requiredFields = ['name', 'start_date', 'end_date'];
      milestoneValid = requiredFields.every(field => !!firstMilestone[field]);

      // Treat milestones as a single field for calculation
      totalFields++;
      if (milestoneValid) {
        completedFields++;
      }
    }
    return { totalFields, completedFields };
  }

  
  // Existing code remains intact...

  // New map to track the specific plan steps' progress
  private progressMap2 = new Map<string, boolean>();

  // Initialize the progress for each step using the enum
  private initializePlanProgress() {
    // Initialize each step as incomplete (false) using the PlanStep enum
    Object.values(PlanStep).forEach((step) => {
      this.progressMap2.set(step, false);
    });
  }

  // Method to update the completion status of a specific plan step
  updateStepProgress(stepName: string, isCompleted: boolean) {
    if (this.progressMap2.has(stepName)) {
      this.progressMap2.set(stepName, isCompleted);
      console.log(`Step '${stepName}' marked as ${isCompleted ? 'completed' : 'incomplete'}`);

      // Recalculate and update the progress based on the updated steps
      const planCompletionPercentage = this.calculatePlanCompletionPercentage();
      this.updateProgress2(planCompletionPercentage);
    } else {
      console.error(`Step '${stepName}' not found in the progress map.`);
    }
  }

  // Calculate the overall completion percentage for the plan steps
  private calculatePlanCompletionPercentage(): number {
    let totalSteps = 0;
    let completedSteps = 0;

    // Loop through the progressMap2 to calculate the completion
    this.progressMap2.forEach((completed) => {
      totalSteps++;
      if (completed) {
        completedSteps++;
      }
    });

    // Return the percentage of completion
    return totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
  }

  // Existing methods remain unchanged...
}
